import { SignRseProcessResponse } from '../../models/sign-rse-api.model';

/** Extrae filename de Content-Disposition (attachment; filename="..."). */
export function parseContentDispositionFileName(header: string | null): string | null {
  if (!header?.trim()) {
    return null;
  }
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header) || /filename=([^;]+)/i.exec(header);
  if (plain?.[1]) {
    return plain[1].trim().replace(/^"|"$/g, '');
  }
  return null;
}

export function normalizeCadesOutputExtension(raw?: string | null): string {
  const fallback = 'p7z';
  if (!raw?.trim()) {
    return fallback;
  }
  let value = raw.trim().toLowerCase();
  if (value.startsWith('.')) {
    value = value.slice(1);
  }
  return value === 'p7m' || value === 'p7z' ? value : fallback;
}

export function stripFileExtension(fileName: string): string {
  const name = fileName.trim();
  const slash = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'));
  const base = slash >= 0 ? name.slice(slash + 1) : name;
  const dot = base.lastIndexOf('.');
  if (dot > 0) {
    return base.slice(0, dot);
  }
  return base || 'documento';
}

/**
 * Nombre de archivo firmado para UI/descarga (CADES vía Sign_RSE).
 * Prioriza Content-Disposition; si no, construye *_firmado.{ext}.
 */
export function resolveRseSignedFileName(
  originalFileName: string,
  process: SignRseProcessResponse | null | undefined,
  contentDispositionFileName: string | null | undefined,
  defaultCadesExtension: string
): string {
  const fromHeader = contentDispositionFileName?.trim();
  if (fromHeader) {
    return fromHeader;
  }
  const base = stripFileExtension(originalFileName);
  if (String(process?.signatureType || '').toUpperCase() === 'CADES') {
    const ext = normalizeCadesOutputExtension(
      process?.outputExtension || defaultCadesExtension
    );
    return `${base}_firmado.${ext}`;
  }
  if (String(process?.signatureType || '').toUpperCase() === 'PADES') {
    return `${base}_firmado.pdf`;
  }
  if (String(process?.signatureType || '').toUpperCase() === 'XADES') {
    return `${base}_firmado.xml`;
  }
  return `${base}_firmado`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * HTML del popup about:blank mientras se obtiene authorizationUrl de RSE.
 * Pasos: Autenticando → Redirigiendo RSE (aún no se firma en esta ventana).
 */
export function buildRseAuthPreparingHtml(opts?: {
  fileName?: string | null;
  iconUrl?: string | null;
}): string {
  const fileName = (opts?.fileName || '').trim();
  const safeName = fileName ? escapeHtml(fileName) : '';
  const iconUrl = (opts?.iconUrl || '').trim();
  const iconBlock = iconUrl
    ? `<img src="${escapeHtml(iconUrl)}" alt="" width="28" height="28" style="display:block;width:28px;height:28px;object-fit:contain;" />`
    : `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
         <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="#f35b04" stroke-width="1.7"/>
         <path d="M14 3v5h5" stroke="#f35b04" stroke-width="1.7"/>
         <path d="M8.5 14.5c1.8-2.2 5.2-2.2 7 0" stroke="#f35b04" stroke-width="1.7" stroke-linecap="round"/>
       </svg>`;

  const fileIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="flex-shrink:0;">
    <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="#f35b04" stroke-width="1.8"/>
    <path d="M14 3v5h5" stroke="#f35b04" stroke-width="1.8"/>
  </svg>`;

  const packetIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="#fff" stroke-width="1.8"/>
    <path d="M14 3v5h5" stroke="#fff" stroke-width="1.8"/>
  </svg>`;

  const filePill = safeName
    ? `<div style="display:inline-flex;align-items:center;gap:6px;max-width:100%;margin:0 auto 14px;padding:5px 10px;
         border-radius:999px;background:#f8fafc;border:1px solid #e2e8f0;color:#475569;font-size:12px;font-weight:500;
         overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
         ${fileIcon}
         <span style="overflow:hidden;text-overflow:ellipsis;">${safeName}</span>
       </div>`
    : '';

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Certicámara · Autenticación</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700&display=swap" rel="stylesheet" />
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; min-height: 100%; }
    body {
      font-family: Inter, system-ui, sans-serif;
      background: #ffffff;
      color: #334155;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }
    .card {
      width: min(440px, 100%);
      background: #fff;
      border-radius: 20px;
      border: 1px solid rgba(226,232,240,.9);
      box-shadow: 0 18px 40px rgba(15,23,42,.08);
      padding: 28px 24px 22px;
      text-align: center;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      margin-bottom: 8px;
    }
    .header-icon {
      width: 40px; height: 40px; border-radius: 12px;
      background: linear-gradient(145deg, #fff7ed, #ffedd5);
      border: 1.5px solid rgba(243,91,4,.35);
      color: #f35b04;
      display: flex; align-items: center; justify-content: center;
    }
    h1 {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: -0.01em;
    }
    .sub {
      margin: 6px 0 18px;
      font-size: 13px;
      color: #64748b;
      line-height: 1.45;
    }
    .journey {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;
      min-height: 72px;
      padding: 4px 4px 8px;
      margin-bottom: 8px;
    }
    .line {
      position: absolute;
      top: 18px; left: 22%; right: 22%;
      height: 3px; border-radius: 999px; background: #e2e8f0; overflow: hidden;
    }
    .line-fill {
      display: block; height: 100%; width: 28%;
      background: linear-gradient(90deg, #f35b04, #fb923c);
      animation: linePulse 1.6s ease-in-out infinite;
    }
    .step {
      position: relative; z-index: 1;
      width: 120px; display: flex; flex-direction: column; align-items: center; gap: 8px;
    }
    .node {
      width: 38px; height: 38px; border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700;
      background: #f8fafc; border: 1.5px solid #e2e8f0; color: #94a3b8;
    }
    .step.active .node {
      background: linear-gradient(145deg, #fff7ed, #ffedd5);
      border-color: rgba(243,91,4,.55); color: #f35b04;
      box-shadow: 0 8px 18px rgba(243,91,4,.18);
      animation: pulse 1.8s ease-in-out infinite;
    }
    .label {
      font-size: 10px; font-weight: 600; letter-spacing: .02em;
      text-transform: uppercase; color: #94a3b8;
      line-height: 1.25; max-width: 110px;
    }
    .step.active .label { color: #c2410c; }
    .packet {
      position: absolute; top: 6px; left: 28%;
      width: 28px; height: 28px; margin-left: -14px; border-radius: 8px;
      background: #f35b04; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px;
      box-shadow: 0 6px 14px rgba(243,91,4,.35);
      animation: bob 1.4s ease-in-out infinite, crawl 2.8s ease-in-out infinite;
      z-index: 2;
    }
    .msg {
      margin: 10px 0 0;
      font-size: 14px; font-weight: 600; color: #334155; line-height: 1.4;
    }
    .tip {
      margin: 8px 0 0;
      font-size: 12.5px; color: #64748b; line-height: 1.45;
    }
    .note {
      margin: 14px 0 0;
      padding: 8px 12px; border-radius: 10px;
      background: rgba(243,91,4,.08); border: 1px solid rgba(243,91,4,.16);
      color: #9a3412; font-size: 12px; font-weight: 600; line-height: 1.35;
    }
    .brand {
      margin: 14px 0 0;
      font-size: 12px; font-weight: 600; color: #94a3b8;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.06); }
    }
    @keyframes bob {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-3px); }
    }
    @keyframes crawl {
      0%, 100% { left: 28%; }
      50% { left: 48%; }
    }
    @keyframes linePulse {
      0%, 100% { width: 22%; opacity: .85; }
      50% { width: 42%; opacity: 1; }
    }
  </style>
</head>
<body>
  <div class="card" role="status" aria-live="polite">
    <div class="header">
      <div class="header-icon">${iconBlock}</div>
      <h1>Firmando documentos</h1>
    </div>
    <p class="sub">Preparando la autenticación segura con Certicámara</p>

    <div class="journey" aria-hidden="true">
      <div class="line"><span class="line-fill"></span></div>
      <div class="step active">
        <span class="node">1</span>
        <span class="label">Autenticando</span>
      </div>
      <div class="step">
        <span class="node">2</span>
        <span class="label">Redirigiendo RSE</span>
      </div>
      <div class="packet">${packetIcon}</div>
    </div>

    ${filePill}

    <p class="msg">En un momento se abrirá el inicio de sesión de Certicámara.</p>
    <p class="tip">Completa tu cédula y contraseña en esta ventana. No la cierres hasta terminar.</p>
    <p class="note">Si el inicio de sesión tarda unos segundos, es normal. Mantén esta ventana abierta.</p>
    <p class="brand">Certicámara</p>
  </div>
</body>
</html>`;
}
